/**
 * Note Editor Component
 * Allows users to create or edit notes via typing or voice recording
 */

import React, { useState, useEffect, useRef } from 'react';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import microphoneTokens from '@amzn/meridian-tokens/base/icon/microphone';
import stopTokens from '@amzn/meridian-tokens/base/icon/stop';
import { useAppSelector, useAppDispatch } from '../../../store/store';
import { saveNote } from '../../../store/sessionSlice';
import { startNoteRecording, stopNoteRecording } from '../../../store/recordingSlice';
import { showSuccess, showError } from '../../../store/toastsSlice';
import { audioService } from '../../../services/audioService';
import { websocketService } from '../../../services/websocketService';
import { FONT_SIZE_BODY, TEXT_COLOR_SECONDARY } from '../cssConstants';
import { useFontSize } from './FontSizeContext';
import { v4 as uuidv4 } from 'uuid';

interface NoteEditorProps {
  sessionId: string;
  existingNote?: {
    note_id: string;
    content: string;
  };
  onCancel?: () => void;
  onSaved?: () => void;
  /** Greys the editor out instead of removing it, for viewers who may read but not add notes. */
  disabled?: boolean;
}

const NoteEditor: React.FC<NoteEditorProps> = ({ 
  sessionId, 
  existingNote,
  onCancel,
  onSaved,
  disabled = false,
}) => {
  const { fontSize } = useFontSize();
  const dispatch = useAppDispatch();
  const { saveNoteStatus } = useAppSelector((state) => state.session);
  const { 
    isRecording: isConversationRecording, 
    isNoteRecording, 
    wsStatus,
    isNoteRecordingDisabled 
  } = useAppSelector((state) => state.recording);
  
  const [content, setContent] = useState(existingNote?.content || '');
  const [partialTranscript, setPartialTranscript] = useState('');
  const [isStopping, setIsStopping] = useState(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  
  const isSaving = saveNoteStatus === 'pending';
  const isEditMode = Boolean(existingNote);
  
  // Disable voice recording if conversation recording is active, cleaning up, or WebSocket disconnected
  const canVoiceRecord = !isConversationRecording && 
                          !isNoteRecordingDisabled &&
                          (wsStatus === 'connected' || !isNoteRecording);

  // Setup WebSocket listener for note transcripts
  useEffect(() => {
    const finishCleanup = () => {
      setIsStopping(false);
      setPartialTranscript('');
      dispatch(stopNoteRecording());
      dispatch(showSuccess('Voice note stopped'));
    };

    if (isNoteRecording || isStopping) {
      websocketService.onNoteTranscriptUpdate((text: string, isPartial: boolean) => {
        if (isPartial) {
          setPartialTranscript(text);
        } else {
          // Final text - add to content
          setContent((prev) => {
            const separator = prev.trim() ? ' ' : '';
            return prev + separator + text;
          });
          setPartialTranscript('');
          
          // If we're in stopping state and received final text, finish cleanup
          if (isStopping) {
            finishCleanup();
          }
        }
      });
    }

    return () => {
      if (isNoteRecording || isStopping) {
        websocketService.removeNoteTranscriptCallback();
      }
    };
  }, [isNoteRecording, isStopping, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isNoteRecording) {
        // Cleanup note recording on unmount
        if (audioService.getIsRecording()) {
          audioService.stopRecording().catch(console.error);
        }
        if (websocketService.isConnected()) {
          websocketService.stopNoteRecording();
        }
        audioService.releaseMicrophone();
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop());
        }
        // CRITICAL: Clear Redux state to prevent buttons from being stuck disabled
        dispatch(stopNoteRecording());
      }
    };
  }, [isNoteRecording, dispatch]);

  const handleSave = async () => {
    if (!content.trim()) {
      dispatch(showError('Note content cannot be empty'));
      return;
    }

    try {
      await dispatch(saveNote({
        sessionId,
        content: content.trim(),
        noteId: existingNote?.note_id
      })).unwrap();
      
      dispatch(showSuccess(isEditMode ? 'Note updated' : 'Note created'));
      
      if (!isEditMode) {
        setContent(''); // Clear for next note
      }
      
      if (onSaved) {
        onSaved();
      }
    } catch (error: any) {
      dispatch(showError(error || 'Failed to save note'));
    }
  };

  const handleCancel = () => {
    setContent(existingNote?.content || '');
    if (onCancel) {
      onCancel();
    }
  };

  const handleVoiceButtonClick = async () => {
    if (isNoteRecording) {
      // Stop recording
      await handleStopNoteRecording();
    } else {
      // Start recording
      await handleStartNoteRecording();
    }
  };

  const handleStartNoteRecording = async () => {
    try {
      // 1. ALWAYS create a fresh WebSocket connection for note recording
      // This prevents reusing conversation recording connections which causes backend confusion
      websocketService.initialize(dispatch);
      const connectionId = `conn-${uuidv4()}`;
      await websocketService.connect(connectionId);

      // 2. Request microphone access
      const stream = await audioService.requestMicrophoneAccess();
      mediaStreamRef.current = stream;

      // 3. Start WebSocket note recording
      const audioFormat = audioService.getAudioFormat();
      const sampleRate = audioService.getSampleRate();
      websocketService.startNoteRecording(audioFormat, sampleRate);

      // 4. Start audio recording (streams to WebSocket)
      await audioService.startRecording(stream);

      // 5. Update Redux state
      dispatch(startNoteRecording());
      dispatch(showSuccess('Voice note started'));
    } catch (error: any) {
      console.error('Failed to start note recording:', error);
      dispatch(showError(error.message || 'Failed to start voice note'));
      
      // Cleanup on error
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
    }
  };

  const handleStopNoteRecording = async () => {
    try {
      // Enter "stopping" state - waiting for final transcription
      setIsStopping(true);
      
      // Stop audio recording (no more audio sent)
      await audioService.stopRecording();

      // Send stop message to backend (backend will send final results)
      websocketService.stopNoteRecording();

      // Release microphone
      audioService.releaseMicrophone();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }

      // DON'T cleanup yet - wait for note_transcript_complete
      // The cleanup happens in the useEffect when final text arrives
    } catch (error: any) {
      console.error('Failed to stop note recording:', error);
      dispatch(showError('Failed to stop voice note'));
      // On error, force cleanup
      setIsStopping(false);
      setPartialTranscript('');
      dispatch(stopNoteRecording());
    }
  };

  // Combine content with partial transcript for display
  const displayValue = content + (partialTranscript ? (content ? ' ' : '') + partialTranscript : '');

  return (
    <div>
      {/* Text Area - always visible */}
      <textarea
        value={displayValue}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value)}
        placeholder="Add note here"
        rows={4}
        disabled={disabled || isNoteRecording}
        style={{ 
          width: '100%', 
          marginBottom: '12px',
          padding: '8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontFamily: 'inherit',
          fontSize: fontSize,
          resize: 'vertical',
        }}
      />

      {/* Action Buttons - all on same row */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        justifyContent: isEditMode ? 'flex-end' : 'space-between', 
        alignItems: 'center' 
      }}>
        {/* Voice recording button - only show when creating new note */}
        {!isEditMode && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button
              size="small"
              onClick={handleVoiceButtonClick}
              disabled={disabled || isNoteRecordingDisabled || isStopping}
              type={isNoteRecording || isStopping ? 'primary' : 'tertiary'}
            >
              {isStopping ? (
                <>
                  <Icon tokens={stopTokens} /> Processing...
                </>
              ) : isNoteRecording ? (
                <>
                  <Icon tokens={stopTokens} /> Stop Listening
                </>
              ) : (
                <>
                  <Icon tokens={microphoneTokens} /> Add Voice Note
                </>
              )}
            </Button>
            {isNoteRecordingDisabled && (
              <span style={{ fontSize: '11px', color: TEXT_COLOR_SECONDARY }}>
                Disabled during adding conversation
              </span>
            )}
          </div>
        )}
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {(isEditMode || onCancel) && (
            <Button onClick={handleCancel} size="small" disabled={disabled || isSaving || isNoteRecording}>
              Cancel
            </Button>
          )}
          <Button 
            onClick={handleSave} 
            size="small"
            type="tertiary"
            disabled={disabled || isSaving || !content.trim() || isNoteRecording || isStopping}
          >
            {isSaving ? 'Saving...' : (isEditMode ? 'Update' : 'Add Note')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NoteEditor;